import { DAY_CELL_HORIZONTAL_PADDING_PX } from './monthlyLayoutConstants';

export const ITEM_ROW_HEIGHT_PX = 20;
export const ITEM_TOP_OFFSET_PX = 10;
export const ITEM_LEFT_OFFSET_PX = DAY_CELL_HORIZONTAL_PADDING_PX;
export const MIN_ROW_HEIGHT_PX = 40;
export const TRACK_GAP_PX = 12;
/** Matches `.monthlyRowItemLayer` padding-bottom. */
export const MONTHLY_ROW_LAYER_PADDING_BOTTOM_PX = 10;
/** Minimum horizontal space between adjacent cards on the same track. */
export const TRACK_ITEM_HORIZONTAL_GAP_PX = 8;

export interface TrackPlacementInput {
  leftPx: number;
  widthPx: number;
  heightPx?: number;
}

export interface DateGroupableTrackPlacementInput extends TrackPlacementInput {
  chronologyMs: number;
}

export function placeItemsInTracks<T extends TrackPlacementInput>(
  items: T[]
): Array<T & { rowIndex: number; topPx: number }> {
  const tracks: Array<{ endPx: number; bottomPx: number; topPx: number }> = [];

  return items.map(entry => {
    const leftPx = entry.leftPx;
    const rightPx = leftPx + entry.widthPx + TRACK_ITEM_HORIZONTAL_GAP_PX;
    const heightPx = entry.heightPx ?? ITEM_ROW_HEIGHT_PX;

    let rowIndex = 0;
    while (rowIndex < tracks.length && leftPx < tracks[rowIndex].endPx) {
      rowIndex += 1;
    }

    let topPx: number;
    if (rowIndex >= tracks.length) {
      if (tracks.length === 0) {
        topPx = ITEM_TOP_OFFSET_PX;
      } else {
        const prevBottom = Math.max(...tracks.map(track => track.bottomPx));
        topPx = prevBottom + TRACK_GAP_PX;
      }
    } else {
      topPx = tracks[rowIndex].topPx;
    }

    const bottomPx = topPx + heightPx;
    const existingTrack = tracks[rowIndex];

    tracks[rowIndex] = {
      endPx: Math.max(existingTrack?.endPx ?? 0, rightPx),
      bottomPx: Math.max(existingTrack?.bottomPx ?? 0, bottomPx),
      topPx: existingTrack?.topPx ?? topPx
    };

    return {
      ...entry,
      rowIndex,
      topPx: tracks[rowIndex].topPx
    };
  });
}

function groupItemsByStartDate<T extends DateGroupableTrackPlacementInput>(
  items: T[]
): T[][] {
  const groups: T[][] = [];
  let currentGroup: T[] = [];
  let currentChronologyMs: number | null = null;

  items.forEach(item => {
    if (
      currentChronologyMs === null ||
      item.chronologyMs !== currentChronologyMs
    ) {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [item];
      currentChronologyMs = item.chronologyMs;
      return;
    }

    currentGroup.push(item);
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

type OccupiedSegment = { leftPx: number; rightPx: number };

function getRightPx(entry: TrackPlacementInput): number {
  return entry.leftPx + entry.widthPx + TRACK_ITEM_HORIZONTAL_GAP_PX;
}

function segmentsOverlap(
  leftA: number,
  rightA: number,
  leftB: number,
  rightB: number
): boolean {
  return leftA < rightB && leftB < rightA;
}

/**
 * Computes per-item vertical offsets so expansion only pushes cards below in the
 * same horizontal column. First-row cards use ITEM_TOP_OFFSET_PX; layer CSS
 * padding-top provides the outer inset.
 */
export function computeColumnAwareRenderTops<
  T extends TrackPlacementInput & { rowIndex: number }
>(items: T[]): Array<T & { renderTopPx: number }> {
  const placed: Array<{
    leftPx: number;
    rightPx: number;
    bottomPx: number;
    rowIndex: number;
  }> = [];

  return items.map(entry => {
    const leftPx = entry.leftPx;
    const rightPx = getRightPx(entry);
    const heightPx = entry.heightPx ?? ITEM_ROW_HEIGHT_PX;

    const overlapping = placed.filter(previous =>
      segmentsOverlap(leftPx, rightPx, previous.leftPx, previous.rightPx)
    );
    const renderTopPx =
      overlapping.length === 0
        ? ITEM_TOP_OFFSET_PX
        : Math.max(...overlapping.map(previous => previous.bottomPx)) +
          TRACK_GAP_PX;

    placed.push({
      leftPx,
      rightPx,
      bottomPx: renderTopPx + heightPx,
      rowIndex: entry.rowIndex
    });

    return { ...entry, renderTopPx };
  });
}

function conflictsWithOccupancy(
  row: number,
  leftPx: number,
  rightPx: number,
  occupancy: Map<number, OccupiedSegment[]>
): boolean {
  const segments = occupancy.get(row) || [];
  return segments.some(segment =>
    segmentsOverlap(leftPx, rightPx, segment.leftPx, segment.rightPx)
  );
}

function canPlaceGroupAtOffset<T extends TrackPlacementInput>(
  groupPlaced: Array<T & { rowIndex: number }>,
  occupancy: Map<number, OccupiedSegment[]>,
  offset: number
): boolean {
  return groupPlaced.every(entry => {
    const row = entry.rowIndex + offset;
    return !conflictsWithOccupancy(
      row,
      entry.leftPx,
      getRightPx(entry),
      occupancy
    );
  });
}

function addToOccupancy(
  placed: Array<{ rowIndex: number; leftPx: number; widthPx: number }>,
  occupancy: Map<number, OccupiedSegment[]>
): void {
  placed.forEach(entry => {
    const segments = occupancy.get(entry.rowIndex) || [];
    segments.push({ leftPx: entry.leftPx, rightPx: getRightPx(entry) });
    occupancy.set(entry.rowIndex, segments);
  });
}

function findMinRowOffset<T extends TrackPlacementInput>(
  groupPlaced: Array<T & { rowIndex: number }>,
  occupancy: Map<number, OccupiedSegment[]>
): number {
  let offset = 0;
  while (!canPlaceGroupAtOffset(groupPlaced, occupancy, offset)) {
    offset += 1;
  }
  return offset;
}

/**
 * Places items in horizontal tracks while keeping all cards that share the
 * same start date in consecutive vertical rows. Each date group is placed as
 * a contiguous block, but groups reuse existing rows when their cards do not
 * overlap horizontally — avoiding wasted vertical space.
 */
export function placeItemsInTracksByDateGroup<
  T extends DateGroupableTrackPlacementInput
>(items: T[]): Array<T & { rowIndex: number; topPx: number }> {
  if (items.length === 0) {
    return [];
  }

  const groups = groupItemsByStartDate(items);
  const occupancy = new Map<number, OccupiedSegment[]>();
  const results: Array<T & { rowIndex: number; topPx: number }> = [];

  groups.forEach(group => {
    const groupPlaced = placeItemsInTracks(group);
    const offset = findMinRowOffset(groupPlaced, occupancy);

    const absolutePlaced = groupPlaced.map(entry => ({
      ...entry,
      rowIndex: entry.rowIndex + offset
    }));

    results.push(...absolutePlaced);
    addToOccupancy(absolutePlaced, occupancy);
  });

  return results;
}
